import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle } from "@/components/admin/kit";
import { TdvPricing, startCheckout } from "@/threedvista/TdvPricing";
import { useAuth } from "@/lib/auth";
import { productFor } from "@/threedvista/product";

const useBase = () => productFor(useAuth().user?.product).base;

const Shell = ({ children }) => <div className="min-h-screen bg-[#0B1220] text-white grid place-items-center p-6"><div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center">{children}</div></div>;

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [st, setSt] = useState("checking");
  const base = useBase();
  useEffect(() => {
    let n = 0;
    let stop = false;
    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setSt("paid"); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setSt("failed"); return; }
      } catch { /* keep polling */ }
      n += 1;
      if (n < 15) setTimeout(poll, 2000); else setSt("slow");
    };
    if (sid) poll(); else setSt("failed");
    return () => { stop = true; };
  }, [sid]);
  return (
    <Shell>
      <div data-testid="payment-success-page">
        {st === "checking" && <><Loader2 className="w-10 h-10 mx-auto animate-spin text-[#C9A227]" /><p className="font-serif text-2xl mt-4">Confirming your payment…</p></>}
        {st === "paid" && <><CheckCircle2 className="w-10 h-10 mx-auto text-emerald-400" /><p className="font-serif text-3xl mt-4" data-testid="payment-paid">You're all set</p><p className="text-sm text-white/70 mt-2">Your plan is active. Add your first tour.</p></>}
        {st === "slow" && <p className="text-sm text-white/70" data-testid="payment-slow">Your payment is still being confirmed. Your plan will switch on in a minute or two.</p>}
        {st === "failed" && <><XCircle className="w-10 h-10 mx-auto text-rose-400" /><p className="font-serif text-2xl mt-4" data-testid="payment-failed">Payment didn't go through</p></>}
        <Link to={`${base}/app`} className="mt-6 h-11 rounded-full bg-[#C9A227] text-slate-950 text-sm font-semibold grid place-items-center" data-testid="payment-go-app">Go to my tours</Link>
      </div>
    </Shell>
  );
}

export function PaymentCancel() {
  const base = useBase();
  return (
    <Shell>
      <div data-testid="payment-cancel-page">
        <p className="font-serif text-2xl">Payment cancelled</p>
        <p className="text-sm text-white/70 mt-2">No money was taken. You can choose a plan any time.</p>
        <Link to={`${base}/app/plan`} className="mt-6 h-11 rounded-full bg-[#C9A227] text-slate-950 text-sm font-semibold grid place-items-center" data-testid="payment-back-plans">Back to plans</Link>
      </div>
    </Shell>
  );
}

export default function TdvPlan() {
  const [a, setA] = useState(null);
  useEffect(() => { api.get("/tdv/account").then((r) => setA(r.data)); }, []);
  if (!a) return null;
  return (
    <div data-testid="tdv-plan-page">
      <PageTitle title="Your plan" sub={`${a.plan_name} · ${a.builds_used} of ${a.tour_limit ?? "unlimited"} builds used`} />
      <div className="rounded-3xl bg-[#0B1220] text-white pt-10 overflow-hidden">
        <TdvPricing currency={a.currency || "USD"} onPick={startCheckout} current={a.plan} />
      </div>
    </div>
  );
}
